import { useEffect, useState } from "react";
import Dropzone from "react-dropzone";
import { useDispatch, useSelector } from "react-redux";
import { Spinner, Toaster } from "@fluentui/react-components";
import DynamicDropzone, { FileWithStatus } from "../../../components/DynamicDropzone/Dropzone";
import ASCButton from "../../../components/Button";
import PermissionGuard from "../../../components/PermissionGuard";
import { useToastMessage } from "../../../components/ToastMessage/ToastMessage";
import { UserPermissionsList } from "../../../config";
import { AppDispatch, RootState } from "../../../redux/store";
import {
  fetchUploadProviders,
  generateBulkValuation,
} from "../../../redux/SystemManager/bulkValuationUploadSlice";

// The mockup's hint text lists .xml/.csv/.xlsx as "all supported" — only .xml
// actually works end-to-end today (bulkvaluations' converter is XML-only), so
// that wording is deliberately narrowed here rather than promising formats
// that would fail downstream.
const ALLOWED_EXTENSION = ".xml";
const MAX_FILE_SIZE_MB = 20; // matches document's MAX_UPLOAD_SIZE_MB default — also passed to DynamicDropzone below so both size gates agree
const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;

const NAVY = "#205393";
const NAVY_DARK = "#184278";
const BORDER = "#e2e5ea";
const BORDER_MID = "#c8cdd5";
const TEXT = "#1a1f2e";
const TEXT_2 = "#475569";
const TEXT_3 = "#94a3b8";
const DANGER = "#dc2626";

const styles: Record<string, React.CSSProperties> = {
  pageTitle: { fontSize: 15, fontWeight: 600, color: TEXT, marginBottom: 16 },
  stepTrack: { display: "flex", alignItems: "center", marginBottom: 20 },
  stepItem: { display: "flex", alignItems: "center", gap: 8 },
  stepCircle: {
    width: 26,
    height: 26,
    borderRadius: "50%",
    background: NAVY,
    border: `2px solid ${NAVY}`,
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11.5,
    fontWeight: 700,
    flexShrink: 0,
  },
  stepLabel: { fontSize: 12, fontWeight: 600, color: NAVY },
  banner: {
    borderRadius: 5,
    padding: "11px 14px",
    marginBottom: 14,
    display: "flex",
    gap: 10,
    alignItems: "flex-start",
    fontSize: 12.5,
    lineHeight: 1.55,
  },
  bannerSuccess: { background: "#f0fdf4", border: "1px solid #bbf7d0", color: "#166534" },
  bannerError: { background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b" },
  bannerTitle: { fontWeight: 600, marginBottom: 1 },
  formCard: {
    background: "#fff",
    border: `1px solid ${BORDER}`,
    borderRadius: 8,
    padding: 20,
    boxShadow: "0 1px 3px rgba(0,0,0,.08)",
    marginBottom: 16,
    maxWidth: 620,
  },
  cardTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: NAVY,
    marginBottom: 14,
    paddingBottom: 10,
    borderBottom: `1px solid ${BORDER}`,
    display: "flex",
    alignItems: "center",
    gap: 7,
  },
  formRow: { display: "grid", gridTemplateColumns: "minmax(0,.42fr) 1fr", gap: 14, marginBottom: 16 },
  fieldLabel: {
    fontSize: 11.5,
    fontWeight: 600,
    color: TEXT_2,
    marginBottom: 5,
    display: "flex",
    alignItems: "center",
    gap: 3,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  required: { color: DANGER, fontSize: 11 },
  selectWrap: { position: "relative" },
  select: {
    width: "100%",
    height: 33,
    border: `1px solid ${BORDER_MID}`,
    borderRadius: 5,
    padding: "0 28px 0 9px",
    fontSize: 13,
    color: TEXT,
    background: "#fff",
    outline: "none",
    appearance: "none",
    fontFamily: "inherit",
    cursor: "pointer",
  },
  selectArrow: { position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: TEXT_3 },
  providerError: { fontSize: 11.5, color: DANGER, marginTop: 5 },
  retryLink: {
    background: "none",
    border: "none",
    padding: 0,
    color: NAVY,
    fontWeight: 600,
    fontSize: "inherit",
    cursor: "pointer",
    textDecoration: "underline",
    fontFamily: "inherit",
  },
  uploadZone: {
    border: `2px dashed ${BORDER_MID}`,
    borderRadius: 8,
    padding: "30px 20px",
    textAlign: "center",
    cursor: "pointer",
    transition: "border-color .2s,background .2s",
  },
  uploadZoneDisabled: { opacity: 0.45, cursor: "not-allowed" },
  uploadIcon: {
    width: 46,
    height: 46,
    borderRadius: 8,
    background: "#e4ecf7",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 10px",
    color: NAVY,
  },
  uploadTitle: { fontSize: 13, fontWeight: 600, color: TEXT, marginBottom: 4 },
  uploadSub: { fontSize: 12, color: TEXT_2 },
  uploadHint: { fontSize: 11.5, color: TEXT_3, marginTop: 5 },
  uploadBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    marginTop: 12,
    height: 30,
    padding: "0 16px",
    background: NAVY,
    color: "#fff",
    border: "none",
    borderRadius: 5,
    fontSize: 12.5,
    fontWeight: 500,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  fmtNote: { fontSize: 11.5, color: TEXT_3, marginTop: 8, display: "flex", alignItems: "center", gap: 5 },
  fmtNoteStrong: { color: TEXT_2, fontWeight: 600 },
  actRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    paddingTop: 16,
    marginTop: 4,
    borderTop: `1px solid ${BORDER}`,
  },
};

const UploadIconSvg = () => (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
    <path
      d="M4 15v2a1 1 0 001 1h12a1 1 0 001-1v-2M11 4v10M7.5 7.5L11 4l3.5 3.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CardTitleIconSvg = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
    <path
      d="M2 11V13h11V11M7.5 2v8M4.5 5l3-3 3 3"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronDownSvg = () => (
  <svg width="10" height="7" viewBox="0 0 10 7" fill="none">
    <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const UploadBtnIconSvg = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
    <path
      d="M2 9V11h9V9M6.5 2v7M4 4.5l2.5-2.5L9 4.5"
      stroke="#fff"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const InfoDotSvg = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <circle cx="6" cy="6" r="5" stroke="#9aa5b8" strokeWidth="1.1" />
    <path d="M6 3.5v3M6 8v.3" stroke="#9aa5b8" strokeWidth="1.1" strokeLinecap="round" />
  </svg>
);

const SuccessBannerIconSvg = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="8" r="7" stroke="#16a34a" strokeWidth="1.3" />
    <path d="M5 8l2 2 4-4" stroke="#16a34a" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ErrorBannerIconSvg = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="8" r="7" stroke="#dc2626" strokeWidth="1.3" />
    <path d="M8 5v3.5M8 10.5v.5" stroke="#dc2626" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

const BulkValuationUploadPage: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const authUser = useSelector((state: RootState) => state.authUser);
  const { providers, providersLoading, providersError } = useSelector(
    (state: RootState) => state.bulkValuationUpload
  );
  const { showToast, toasterId } = useToastMessage();

  useEffect(() => {
    dispatch(fetchUploadProviders());
  }, [dispatch]);

  // `provider` holds the DataProvider name (e.g. "Manual-Elevate") — the value
  // actually submitted to the backend. The dropdown displays each option's
  // InvestmentProvider name ("Elevate") instead; built generically off whatever
  // upload-eligible providers the backend returns, not hardcoded to Elevate.
  const [provider, setProvider] = useState("");
  const [rawFiles, setRawFiles] = useState<File[]>([]);
  const [scannedFiles, setScannedFiles] = useState<FileWithStatus[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [banner, setBanner] = useState<{ type: "success" | "error"; message: string } | null>(null);

  const selectedProvider = providers.find((p) => p.dataProviderName === provider);
  const pendingFile = scannedFiles[0];
  const canSubmit = Boolean(provider && pendingFile?.status === "success" && pendingFile?.objectId);

  const handleFileDrop = (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      setBanner({ type: "error", message: "File exceeds the maximum allowed size." });
      return;
    }

    setBanner(null);
    setRawFiles([file]);
    setScannedFiles([]);
  };

  // react-dropzone's `accept` filters non-matching files into fileRejections
  // before onDrop ever sees them, so this — not the old in-handler extension
  // check — is what actually fires for a wrong file type. Without it, picking
  // a non-.xml file produced no feedback at all.
  const handleFileRejected = () => {
    setBanner({ type: "error", message: `Only ${ALLOWED_EXTENSION} files are supported.` });
  };

  const handleFileRemoved = (removedFile: File) => {
    setRawFiles((prev) => prev.filter((f) => f !== removedFile));
    setScannedFiles([]);
  };

  const resetForm = () => {
    setProvider("");
    setRawFiles([]);
    setScannedFiles([]);
    setBanner(null);
  };

  const handleUploadAndProcess = async () => {
    if (!canSubmit || !pendingFile?.objectId) return;

    setSubmitting(true);
    try {
      // No client-side finalize call — orchestration's /generate endpoint
      // finalizes the quarantined upload itself, synchronously, before the job
      // is even created (it re-checks scan status server-side, so it's safe to
      // hand off right after a clean scan). This does mean the request below
      // can take a little longer than a typical API call.
      const result = await dispatch(
        generateBulkValuation({
          objectId: pendingFile.objectId,
          provider,
          fileName: pendingFile.file.name,
        })
      );
      if (!generateBulkValuation.fulfilled.match(result)) {
        throw new Error((result.payload as string) || "Failed to start processing.");
      }

      setBanner({
        type: "success",
        message:
          "Valuation processing is in progress. Please check Utilities → Job to track the status.",
      });
      showToast("Job created", "Check Utilities → Job for status.", "success");
      setRawFiles([]);
      setScannedFiles([]);
    } catch (err: any) {
      setBanner({ type: "error", message: err?.message || "File upload failed, please check the file." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Toaster toasterId={toasterId} position="top-end" pauseOnHover timeout={3000} />

      <div style={styles.pageTitle}>Manual Bulk Valuation</div>

      <div style={styles.stepTrack}>
        <div style={styles.stepItem}>
          <div style={styles.stepCircle}>1</div>
          <div style={styles.stepLabel}>Upload File</div>
        </div>
      </div>

      {banner && (
        <div style={{ ...styles.banner, ...(banner.type === "success" ? styles.bannerSuccess : styles.bannerError) }}>
          {banner.type === "success" ? <SuccessBannerIconSvg /> : <ErrorBannerIconSvg />}
          <div>
            <div style={styles.bannerTitle}>{banner.type === "success" ? "File Uploaded Successfully" : "Upload Failed"}</div>
            {banner.message}
          </div>
        </div>
      )}

      <PermissionGuard permission={UserPermissionsList.ALLOW_UPDATE_MASTER_DATA}>
      <div style={styles.formCard}>
        <div style={styles.cardTitle}>
          <CardTitleIconSvg />
          Step 1 — Upload Valuation File
        </div>

        <div style={styles.formRow}>
          <div>
            <div style={styles.fieldLabel}>
              Provider <span style={styles.required}>*</span>
            </div>
            <div style={styles.selectWrap}>
              <select
                aria-label="Provider"
                style={styles.select}
                value={provider}
                disabled={providersLoading}
                onChange={(e) => {
                  setProvider(e.target.value);
                  setRawFiles([]);
                  setScannedFiles([]);
                  setBanner(null);
                }}
              >
                <option value="" disabled>
                  {providersLoading ? "Loading providers…" : "Select Provider"}
                </option>
                {providers.map((p) => (
                  <option key={p.dataProviderName} value={p.dataProviderName}>
                    {p.investmentProviderName}
                  </option>
                ))}
              </select>
              <span style={styles.selectArrow}>
                <ChevronDownSvg />
              </span>
            </div>
            {providersError && (
              <div style={styles.providerError}>
                {providersError}{" "}
                <button
                  type="button"
                  style={styles.retryLink}
                  onClick={() => dispatch(fetchUploadProviders())}
                >
                  Retry
                </button>
              </div>
            )}
            {!providersLoading && !providersError && providers.length === 0 && (
              <div style={styles.providerError}>No upload-eligible providers are configured.</div>
            )}
          </div>
        </div>

        <div style={styles.fieldLabel}>
          Valuation File <span style={styles.required}>*</span>
        </div>

        {rawFiles.length === 0 ? (
          <Dropzone
            multiple={false}
            accept={{ "text/xml": [".xml"] }}
            onDrop={handleFileDrop}
            onDropRejected={handleFileRejected}
            disabled={!provider}
          >
            {({ getRootProps, getInputProps, open }) => (
              <div
                {...getRootProps()}
                style={{ ...styles.uploadZone, ...(!provider ? styles.uploadZoneDisabled : {}) }}
              >
                <input {...getInputProps()} />
                <div style={styles.uploadIcon}>
                  <UploadIconSvg />
                </div>
                <div style={styles.uploadTitle}>
                  {selectedProvider
                    ? `Please upload the valuation file from the ${selectedProvider.investmentProviderName} Portal`
                    : "Select a provider above to begin"}
                </div>
                <div style={styles.uploadSub}>
                  Drag &amp; drop or <strong>browse</strong> to choose a file
                </div>
                <div style={styles.uploadHint}>.xml only</div>
                <button
                  type="button"
                  style={styles.uploadBtn}
                  disabled={!provider}
                  onClick={(e) => {
                    e.stopPropagation();
                    open();
                  }}
                >
                  <UploadBtnIconSvg />
                  Choose File
                </button>
              </div>
            )}
          </Dropzone>
        ) : (
          <DynamicDropzone
            multiple={false}
            externalFiles={rawFiles}
            onFilesChange={setScannedFiles}
            onFileRemoved={handleFileRemoved}
            userId={authUser?.userId}
            maxFileSizeMB={MAX_FILE_SIZE_MB}
          />
        )}

        <div style={styles.fmtNote}>
          <InfoDotSvg />
          Accepted format: <strong style={styles.fmtNoteStrong}>.xml</strong>
        </div>

        <div style={styles.actRow}>
          <ASCButton
            shape="rounded"
            appearance="primary"
            icon={submitting ? <Spinner size="tiny" /> : <UploadBtnIconSvg />}
            label={submitting ? "Uploading…" : "Upload & Process"}
            disabled={!canSubmit || submitting}
            onClick={handleUploadAndProcess}
          />
          <ASCButton shape="rounded" appearance="secondary" label="Clear" onClick={resetForm} />
        </div>
      </div>
      </PermissionGuard>
    </div>
  );
};

export default BulkValuationUploadPage;
